import { useCallback, useEffect, useState } from 'react';
import { getMicrosoftIntegrationStatus } from '../utils/api';
import * as appStorage from '../utils/appIndexedDB';
import { openSettings, SETTINGS_CLOSED_EVENT } from '../utils/settingsModal';
import './MailNotifyCard.css';

/**
 * 「信件即時通知」頁的第 1 步：Outlook 連結（AGUI-1113）。
 *
 * ⛔ 連結正常時<b>整張卡不顯示</b>（使用者裁決）：這頁的主角是訂閱與規則，
 * 已經連好的服務不必再佔一列。只有未連結、失效（授權過期或被撤銷）或查不到時才出現。
 *
 * 連結與解除一律回到 設定 → 第三方服務 處理，這頁只放「前往設定」，
 * 不再各自複製一組按鈕；設定視窗關閉時重查狀態，連好了這張卡就自動消失。
 *
 * 連結狀態由頁面層的 {@link useOutlookLink} 持有，因為訂閱卡也要靠它決定「訂閱」能不能按。
 */

/**
 * @returns {{ link: null|'linked'|'unlinked'|'expired'|'unknown', checking: boolean, refresh: () => void, sync: () => Promise<void>,
 *   check: () => Promise<'linked'|'unlinked'|'expired'>, apply: (link: string) => void }}
 *   `null`＝查詢中；`expired`＝連結過但目前不是 active（授權過期、被撤銷）；
 *   `unknown`＝查詢失敗（⛔ 不可當成未連結）；
 *   `checking`＝這一輪查詢還沒回來（link 可能是沿用上次的結果，AGUI-1259 用來擋規則區閃出）
 */
/**
 * 上次查到的連結狀態（AGUI-1230）。
 *
 * 每次點選單進到這頁都會重新掛載、重查（AGUI-1192），而 /web/integrations/microsoft/status 每次都要
 * 先 sync 再查 GenAIus authgateway，SIT 實測伺服器端 0.3～1 秒；這段期間狀態是「查詢中」，
 * 「訂閱」就灰著等它（「解除訂閱」不看 Outlook 連結，所以沒這問題）。
 * 改成先沿用上次的結果、背景再查，查回來不同再更新（stale-while-revalidate）。
 *
 * ⛔ 以登入 token 當 key：同一分頁換帳號時不可沿用前一個人的結果。沒有 token 就不快取。
 * token 換發後 key 不同，那一次會回到「查詢中」，可接受。查詢失敗不寫入，也會清掉舊的結果。
 * 只存在記憶體：F5 後第一次進頁仍要等一次查詢。
 */
let cachedLink = null; // { token, link }

const currentToken = () => {
  try {
    return appStorage.getItemSync('mygenie_token') || null;
  } catch {
    return null;
  }
};

const readCachedLink = () => {
  const token = currentToken();
  return token && cachedLink?.token === token ? cachedLink.link : null;
};

export function useOutlookLink() {
  const [link, setLink] = useState(readCachedLink);
  const [seq, setSeq] = useState(0);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // 有上次的結果就先沿用，不退回「查詢中」
    setLink(readCachedLink());
    setChecking(true);
    getMicrosoftIntegrationStatus()
      .then((result) => {
        if (cancelled) return;
        // ⛔ 回 null（非 PCS 網域被後端擋下）要當「未連結」，不可當查詢失敗（AC-SUB-01-12）
        const status = result?.types?.outlook?.status;
        const next = status === 'active' ? 'linked' : status ? 'expired' : 'unlinked';
        const token = currentToken();
        cachedLink = token ? { token, link: next } : null;
        setLink(next);
        setChecking(false);
      })
      .catch(() => {
        cachedLink = null;
        if (cancelled) return;
        setLink('unknown');
        setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [seq]);

  const refresh = useCallback(() => setSeq((n) => n + 1), []);

  /**
   * 只查不套用（10-20）：新增表單開著時，查到的結果先存著、表單關閉才 {@link apply}，
   * 免得查到「未連結」把規則區連同填到一半的表單一起收掉。查詢失敗時丟錯。
   */
  const check = useCallback(async () => {
    const result = await getMicrosoftIntegrationStatus();
    const status = result?.types?.outlook?.status;
    return status === 'active' ? 'linked' : status ? 'expired' : 'unlinked';
  }, []);

  const apply = useCallback((next) => {
    const token = currentToken();
    cachedLink = token ? { token, link: next } : null;
    setLink(next);
  }, []);

  /**
   * 背景同步（10-20，每分鐘一次）：不進入「查詢中」、不收起規則區；查到不同才更新。
   * 查詢失敗時維持目前的結果，不切成「查不到」——偶發的一次失敗不該讓畫面整塊消失。
   */
  const sync = useCallback(async () => {
    try {
      apply(await check());
    } catch {
      // 維持目前狀態，下一輪再試
    }
  }, [apply, check]);

  // 使用者從「前往設定」連好 Outlook、關掉設定視窗後，不必重新整理頁面
  useEffect(() => {
    window.addEventListener(SETTINGS_CLOSED_EVENT, refresh);
    return () => window.removeEventListener(SETTINGS_CLOSED_EVENT, refresh);
  }, [refresh]);

  return { link, checking, refresh, sync, check, apply };
}

/**
 * 「失效」（連結過但目前不是 active）在畫面上一律講成「未連結」（會議決議）：設定頁的第三方服務沒有「已失效」這個狀態，
 * 講失效只會讓使用者困惑；「前往設定」只負責帶過去完成連結，不必強調為什麼斷了。
 */
const UNLINKED_TEXT = { label: '未連結', desc: '尚未連結 Outlook，連結後才能訂閱Outlook即時通知。' };
const LINK_TEXT = {
  unlinked: UNLINKED_TEXT,
  expired: UNLINKED_TEXT,
};

/** 手機版的第三方服務連結本來就只限網頁版（openspec connector-mobile-web-only-notice） */
export const MOBILE_HINT = '為了守護您的個人資料安全，第三方服務(如：Outlook、OneDrive) 的連結設定，目前只限在『網頁版』進行。';

function OutlookIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none">
      <rect width="24" height="24" rx="4" fill="#0078D4" />
      <rect x="3.5" y="7.5" width="17" height="11" rx="1.5" fill="white" />
      <path d="M3.5 9.5 L12 14.5 L20.5 9.5" stroke="#0078D4" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function OutlookConnectorCard({ link, onRefresh, isMobile = false }) {
  // 查詢中也不畫：多數使用者是已連結，先閃一下卡片再消失反而干擾
  if (link === null || link === 'linked') return null;

  const text = LINK_TEXT[link];
  return (
    <div className="mail-alert-task outlook-connector-card">
      <div className="mail-alert-task__row">
        <div className="mail-alert-task__icon">
          <OutlookIcon />
        </div>

        <div className="mail-alert-task__body">
          <div className="mail-alert-task__title-row">
            <span className="mail-alert-task__title">Outlook</span>
            {text && (
              <span className="mail-alert-task__state mail-alert-task__state--disabled">{text.label}</span>
            )}
          </div>
          <span className="mail-alert-task__desc">{text ? text.desc : '目前無法取得 Outlook 連結狀態。'}</span>
        </div>

        {!isMobile && (
          <div className="connector-pill-group">
            {link === 'unknown' ? (
              <button type="button" className="connector-pill" onClick={onRefresh}>
                重新整理
              </button>
            ) : (
              <button
                type="button"
                className="connector-pill connector-pill--connect"
                onClick={() => openSettings('connector')}
              >
                前往設定
              </button>
            )}
          </div>
        )}
      </div>

      {/* 手機版說明放在卡片下方而不是按鈕位置（10-06），與訂閱卡同一種灰框 */}
      {isMobile && (
        <div className="mail-alert-task__notice mail-alert-task__notice--info">
          <span className="mail-alert-task__notice-text">{MOBILE_HINT}</span>
        </div>
      )}
    </div>
  );
}
